import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { usePostSocial } from "./use-post-social";
import type { DraftVariations } from "../types/creator-studio";
import { PostContentPayload } from "../types/post-content";
import {
  MAX_IMAGES_MAP,
  MAX_VIDEOS_MAP,
  PLATFORM_REQUIREMENTS,
  PlatformId,
  X_PREMIUM_MAX_CHARACTERS,
  X_STANDARD_MAX_CHARACTERS,
  isXPlatformId,
  urlToFile,
  validateTextContent,
  validatePlatform,
} from "../utils/platform-post-util";

import { BrandDetails } from "../types/add-brand-types";
import { ContentLog } from "../types/dashboard";
import { dispatchWeeklyPlanRefresh } from "../utils/weekly-plan-events";
import { trackEvent } from "../utils/firebase-analytics";

export type HistoryViewMode = "draft" | "scheduled" | "published" | null;

export interface UseStep3Options {
  selectedVariation?: DraftVariations;
  socialPlatforms: ReadonlyArray<{
    id: string;
    name: string;
    icon: string;
  }>;
  brandId: string;
  brandDetails: BrandDetails;
  historyItem?: ContentLog;
  onPostSuccess?: () => void;
  prefilledScheduleDate?: Date;
  prefilledPlatforms?: string[];
  weeklyPlanItemId?: string;
}

export function useStep3(options: UseStep3Options) {
  const {
    selectedVariation,
    brandId,
    socialPlatforms,
    brandDetails,
    historyItem,
    onPostSuccess,
    prefilledScheduleDate,
    prefilledPlatforms,
    weeklyPlanItemId,
  } = options;

  const viewMode: HistoryViewMode = historyItem
    ? (historyItem.status.toLowerCase() as "draft" | "scheduled" | "published")
    : null;

  const {
    postTitle,
    setPostTitle,
    postBody,
    setPostBody,
    tweetBody,
    setTweetBody,
    scheduleOption,
    setScheduleOption,
    togglePlatform,
    selectedPlatforms,
    mediaFiles: postSocialMediaFiles,
    mediaPreviewUrls,
    mediaMetadata,
    handleMediaUpload,
    removeMedia,
    reorderMedia,
    scheduledDate,
    setScheduledDate,
    createPost,
    updatePost,
    uploadMedia,
    threadEnabled,
    setThreadEnabled,
    threadTweets,
    setThreadTweets,
    isStory,
    setIsStory,
    subreddits,
    setSubreddits,
    searchSubreddit,
  } = usePostSocial(
    brandDetails,
    selectedVariation,
    historyItem?.content,
    prefilledScheduleDate,
    prefilledPlatforms
  );

  const [activeFormTab, setActiveFormTab] = useState<string>(
    historyItem?.content?.platforms?.[0] ?? "linkedIn"
  );
  const [showPlatformSelector, setShowPlatformSelector] = useState(false);
  const [useCommonMediaForAllPlatforms, setUseCommonMediaForAllPlatforms] =
    useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isLaunching, setIsLaunching] = useState(false);
  const [xLongFormEnabled, setXLongFormEnabled] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "error" | "warning" | "success";
    id: number;
  } | null>(null);

  const xMaxCharacters = xLongFormEnabled
    ? X_PREMIUM_MAX_CHARACTERS
    : X_STANDARD_MAX_CHARACTERS;

  const getTextValidationOptions = useCallback(
    (platform: string) =>
      isXPlatformId(platform) ? { maxCharacters: xMaxCharacters } : undefined,
    [xMaxCharacters]
  );

  useEffect(() => {
    if (
      selectedPlatforms.length > 0 &&
      !selectedPlatforms.includes(activeFormTab)
    ) {
      setActiveFormTab(selectedPlatforms[0]);
    }
  }, [selectedPlatforms, activeFormTab]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".platform-selector-dropdown") &&
        !target.closest(".add-platform-btn")
      ) {
        setShowPlatformSelector(false);
      }
    };
    if (showPlatformSelector) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPlatformSelector]);

  const platformValidations = useMemo(() => {
    const validations: Record<string, { isValid: boolean; errors: string[] }> =
      {};
    selectedPlatforms.forEach((platform) => {
      const platformReqs = PLATFORM_REQUIREMENTS[platform as PlatformId];
      if (!platformReqs) {
        validations[platform] = {
          isValid: false,
          errors: ["Unknown platform"],
        };
        return;
      }
      const maxMedia = Math.max(
        platformReqs.maxImages + platformReqs.maxVideos,
        10
      );
      const enabledMedia = mediaMetadata.slice(0, maxMedia);
      validations[platform] = validatePlatform(platform, enabledMedia, {
        isStory: platform === "instagram" ? isStory : undefined,
      });
    });
    return validations;
  }, [selectedPlatforms, mediaMetadata, isStory]);

  const validationIssues = useMemo(() => {
    const issues: string[] = [];
    if (selectedPlatforms.length === 0) {
      issues.push("Select at least one platform");
    }
    const descriptionRequiredPlatforms = selectedPlatforms.filter(
      (platform) =>
        !isXPlatformId(platform) && !(platform === "instagram" && isStory)
    );
    const hasNonTwitterPlatform = descriptionRequiredPlatforms.length > 0;
    const hasTwitter = selectedPlatforms.some((platform) =>
      isXPlatformId(platform)
    );
    if (hasNonTwitterPlatform && !postBody?.trim()) {
      issues.push("Post description is required");
    }
    if (hasTwitter && !tweetBody?.trim()) {
      issues.push("Tweet content is required");
    }
    const requiresTitle = selectedPlatforms.some((p) =>
      ["reddit", "linkedIn", "linkedin"].includes(p)
    );
    if (requiresTitle && !postTitle?.trim()) {
      const platforms = selectedPlatforms
        .filter((p) => ["reddit", "linkedIn", "linkedin"].includes(p))
        .map((p) => socialPlatforms.find((sp) => sp.id === p)?.name)
        .filter(Boolean)
        .join(", ");
      issues.push(`Post title is required for ${platforms}`);
    }
    if (scheduleOption === "later" && !scheduledDate) {
      issues.push("Scheduled date and time is required");
    }
    if (
      selectedPlatforms.includes("reddit") &&
      (!subreddits || subreddits.length === 0)
    ) {
      issues.push("At least one subreddit is required for Reddit");
    }
    Object.entries(platformValidations).forEach(([platform, validation]) => {
      if (!validation.isValid) {
        const platformName =
          socialPlatforms.find((p) => p.id === platform)?.name ?? platform;
        validation.errors.forEach((error) => {
          issues.push(`${platformName}: ${error}`);
        });
      }
    });
    selectedPlatforms.forEach((platform) => {
      const text =
        isXPlatformId(platform) ? tweetBody : postBody;
      if (!text?.trim()) return;
      const textValidation = validateTextContent(
        platform,
        text,
        getTextValidationOptions(platform)
      );
      if (!textValidation.isValid) {
        const platformName =
          socialPlatforms.find((p) => p.id === platform)?.name ?? platform;
        textValidation.errors.forEach((error) => {
          issues.push(`${platformName}: ${error}`);
        });
      }
    });
    return issues;
  }, [
    selectedPlatforms,
    platformValidations,
    postBody,
    tweetBody,
    postTitle,
    subreddits,
    scheduleOption,
    scheduledDate,
    socialPlatforms,
    isStory,
    getTextValidationOptions,
  ]);

  const isLaunchDisabled = useMemo(
    () => validationIssues.length > 0,
    [validationIssues]
  );

  const getMaxImages = useCallback((platform: string): number => {
    return MAX_IMAGES_MAP[platform] ?? 10;
  }, []);

  const getMaxVideos = useCallback((platform: string): number => {
    return MAX_VIDEOS_MAP[platform] ?? 1;
  }, []);

  const submitPost = useCallback(
    async (isDraft: boolean) => {
      const descriptionRequiredPlatforms = selectedPlatforms.filter(
        (platform) =>
          !isXPlatformId(platform) && !(platform === "instagram" && isStory)
      );
      const hasNonTwitter = descriptionRequiredPlatforms.length > 0;
      const hasTwitter = selectedPlatforms.some((platform) =>
        isXPlatformId(platform)
      );
      if (hasNonTwitter && !postBody?.trim()) {
        setToast({
          message: "Post description is required",
          type: "error",
          id: Date.now(),
        });
        return;
      }
      if (hasTwitter && !tweetBody?.trim()) {
        setToast({
          message: "Tweet content is required",
          type: "error",
          id: Date.now(),
        });
        return;
      }
      const requiresTitle = selectedPlatforms.some((p) =>
        ["reddit", "linkedIn", "linkedin"].includes(p)
      );
      if (requiresTitle && !postTitle?.trim()) {
        setToast({
          message: "Post title is required for Reddit and LinkedIn",
          type: "error",
          id: Date.now(),
        });
        return;
      }
      if (scheduleOption === "later" && !scheduledDate) {
        setToast({
          message: "Please select a date and time for scheduling",
          type: "error",
          id: Date.now(),
        });
        return;
      }
      if (scheduleOption === "later" && scheduledDate && scheduledDate < new Date()) {
        setToast({
          message: "Scheduled date must be in the future",
          type: "error",
          id: Date.now(),
        });
        return;
      }
      const failedPlatforms = Object.entries(platformValidations).filter(
        ([, v]) => !v.isValid
      );
      if (failedPlatforms.length > 0) {
        const [platform, validation] = failedPlatforms[0];
        const platformName =
          socialPlatforms.find((p) => p.id === platform)?.name ?? platform;
        setToast({
          message: `${platformName} does not meet requirements: ${validation.errors[0]}`,
          type: "error",
          id: Date.now(),
        });
        return;
      }
      const failedTextPlatform = selectedPlatforms.find((platform) => {
        const text =
          isXPlatformId(platform) ? tweetBody : postBody;
        if (!text?.trim()) return false;
        return !validateTextContent(
          platform,
          text,
          getTextValidationOptions(platform)
        ).isValid;
      });
      if (failedTextPlatform) {
        const platformName =
          socialPlatforms.find((p) => p.id === failedTextPlatform)?.name ??
          failedTextPlatform;
        const text =
          isXPlatformId(failedTextPlatform) ? tweetBody : postBody;
        const [error] = validateTextContent(
          failedTextPlatform,
          text,
          getTextValidationOptions(failedTextPlatform)
        ).errors;
        setToast({
          message: `${platformName}: ${error}`,
          type: "error",
          id: Date.now(),
        });
        return;
      }

      setIsLaunching(true);
      const mediaFiles: { type: string; url: string }[] = [];
      try {
        for (let i = 0; i < mediaPreviewUrls.length; i++) {
          let fileToUpload: File;
          if (postSocialMediaFiles[i]) {
            fileToUpload = postSocialMediaFiles[i];
          } else {
            fileToUpload = await urlToFile(
              mediaPreviewUrls[i],
              `media-${i}`,
              i
            );
          }
          const uploaded = await uploadMedia([fileToUpload]);
          if (uploaded) {
            mediaFiles.push(uploaded);
          }
        }
        const payload: PostContentPayload = {
          title: postTitle,
          content: hasNonTwitter ? postBody : tweetBody,
          media: mediaFiles,
          scheduled: scheduleOption === "later",
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          scheduledFor:
            scheduleOption === "later" && scheduledDate
              ? scheduledDate.toISOString().replace(/\.\d{3}Z$/, "Z")
              : null,
          platforms: selectedPlatforms,
          draft: isDraft,
          brandId: brandId,
          brandName: brandDetails.brand_name,
          threads:
            selectedPlatforms.some((platform) => isXPlatformId(platform)) &&
            threadEnabled
              ? [tweetBody, ...threadTweets.filter((t) => t.trim())]
              : [],
          story: isStory,
          subreddits:
            selectedPlatforms.includes("reddit") && subreddits?.length
              ? subreddits
              : undefined,
          weeklyPlanItemId:
            weeklyPlanItemId ?? historyItem?.content?.weeklyPlanItemId ?? null,
          generationMetadata:
            selectedVariation?.generationMetadata ??
            historyItem?.content?.generationMetadata,
        };
        const response = historyItem
          ? await updatePost(payload, historyItem.post_id, historyItem.id)
          : await createPost(payload);
        if (response) {
          trackEvent("post_submission_completed", {
            action: isDraft
              ? "draft"
              : payload.scheduled
              ? "schedule"
              : historyItem
              ? "update"
              : "publish",
            provider: "native",
            platform_count: selectedPlatforms.length,
            has_media: mediaFiles.length > 0,
            has_weekly_plan_item: Boolean(payload.weeklyPlanItemId),
          });
          if (weeklyPlanItemId || historyItem?.content?.weeklyPlanItemId) {
            dispatchWeeklyPlanRefresh(brandId);
          }
          const successMessage = isDraft
            ? "Draft saved successfully"
            : payload.scheduled
            ? "Post scheduled successfully"
            : historyItem
            ? "Post updated successfully"
            : "Post published successfully";
          setToast({
            message: successMessage,
            type: "success",
            id: Date.now(),
          });
          setTimeout(() => {
            setIsLaunching(false);
            onPostSuccess?.();
          }, 1500);
        } else {
          trackEvent("post_submission_failed", {
            action: isDraft
              ? "draft"
              : payload.scheduled
              ? "schedule"
              : historyItem
              ? "update"
              : "publish",
            provider: "native",
            platform_count: selectedPlatforms.length,
          });
          const errorMessage = isDraft
            ? "Failed to save draft"
            : payload.scheduled
            ? "Failed to schedule post"
            : historyItem
            ? "Failed to update post"
            : "Failed to publish post";
          setToast({
            message: errorMessage,
            type: "error",
            id: Date.now(),
          });
          setIsLaunching(false);
        }
      } catch {
        trackEvent("post_submission_failed", {
          action: isDraft
            ? "draft"
            : scheduleOption === "later"
            ? "schedule"
            : historyItem
            ? "update"
            : "publish",
          provider: "native",
          platform_count: selectedPlatforms.length,
        });
        setIsLaunching(false);
      }
    },
    [
      postBody,
      tweetBody,
      selectedPlatforms,
      postTitle,
      scheduleOption,
      scheduledDate,
      platformValidations,
      socialPlatforms,
      brandId,
      brandDetails.brand_name,
      createPost,
      updatePost,
      historyItem,
      selectedVariation?.generationMetadata,
      weeklyPlanItemId,
      mediaPreviewUrls,
      postSocialMediaFiles,
      uploadMedia,
      threadEnabled,
      threadTweets,
      isStory,
      subreddits,
      onPostSuccess,
      getTextValidationOptions,
    ]
  );

  const handleLaunch = useCallback(() => submitPost(false), [submitPost]);
  const handleSaveDraft = useCallback(() => submitPost(true), [submitPost]);

  const processFilesForUpload = useCallback(
    (files: File[]) => {
      const platformName = socialPlatforms.find(
        (p) => p.id === activeFormTab
      )?.name;
      const currentImages = mediaMetadata.filter(
        (m) => m.type === "image"
      ).length;
      const currentVideos = mediaMetadata.filter(
        (m) => m.type === "video"
      ).length;
      const newImages = files.filter((f) => f.type.startsWith("image/"));
      const newVideos = files.filter((f) => f.type.startsWith("video/"));
      const maxImages = getMaxImages(activeFormTab);
      const maxVideos = getMaxVideos(activeFormTab);

      if (
        newVideos.length > 0 &&
        currentVideos + newVideos.length > maxVideos
      ) {
        if (maxVideos === 0) {
          setToast({
            message: `Videos are not supported for ${platformName}`,
            type: "warning",
            id: Date.now(),
          });
          return;
        }
        const availableVideoSlots = maxVideos - currentVideos;
        if (availableVideoSlots <= 0) {
          setToast({
            message: `Maximum ${maxVideos} ${
              maxVideos === 1 ? "video" : "videos"
            } allowed for ${platformName}`,
            type: "warning",
            id: Date.now(),
          });
          return;
        }
        setToast({
          message: `You can only add ${availableVideoSlots} more ${
            availableVideoSlots === 1 ? "video" : "videos"
          } for ${platformName}`,
          type: "warning",
          id: Date.now(),
        });
        return;
      }

      if (
        newImages.length > 0 &&
        currentImages + newImages.length > maxImages
      ) {
        const availableImageSlots = maxImages - currentImages;
        if (availableImageSlots <= 0) {
          setToast({
            message: `Maximum ${maxImages} ${
              maxImages === 1 ? "image" : "images"
            } allowed for ${platformName}`,
            type: "warning",
            id: Date.now(),
          });
          return;
        }
        setToast({
          message: `You can only add ${availableImageSlots} more ${
            availableImageSlots === 1 ? "image" : "images"
          } for ${platformName}`,
          type: "warning",
          id: Date.now(),
        });
        handleMediaUpload([
          ...newVideos,
          ...newImages.slice(0, availableImageSlots),
        ]);
        return;
      }
      handleMediaUpload(files);
    },
    [
      activeFormTab,
      mediaMetadata,
      getMaxImages,
      getMaxVideos,
      handleMediaUpload,
      socialPlatforms,
    ]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.length) {
        processFilesForUpload(Array.from(event.target.files));
      }
    },
    [processFilesForUpload]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (event.dataTransfer.files?.length) {
        processFilesForUpload(Array.from(event.dataTransfer.files));
      }
    },
    [processFilesForUpload]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    []
  );

  const handleImageDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, index: number) => {
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = "move";
    },
    []
  );

  const handleImageDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    },
    []
  );

  const handleImageDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      e.stopPropagation();
      if (draggedIndex != null && draggedIndex !== dropIndex) {
        reorderMedia(draggedIndex, dropIndex);
      }
      setDraggedIndex(null);
    },
    [draggedIndex, reorderMedia]
  );

  const handleImageDragEnd = useCallback(() => {
    setDraggedIndex(null);
  }, []);

  const renderTextWithHashtags = useCallback((text: string) => {
    const lines = text.split("\n");
    return lines.map((line, lineIndex) =>
      React.createElement(
        React.Fragment,
        { key: lineIndex },
        ...line.split(/(#\w+)/g).map((part, partIndex) => {
          const key = `${lineIndex}-${partIndex}`;
          if (part.startsWith("#")) {
            return React.createElement(
              "span",
              { key, style: { color: "#1d9bf0" } },
              part
            );
          }
          return React.createElement("span", { key }, part);
        }),
        lineIndex < lines.length - 1
          ? React.createElement("br", { key: `${lineIndex}-br` })
          : null
      )
    );
  }, []);

  return {
    postTitle,
    setPostTitle,
    postBody,
    setPostBody,
    tweetBody,
    setTweetBody,
    scheduleOption,
    setScheduleOption,
    togglePlatform,
    selectedPlatforms,
    mediaPreviewUrls,
    mediaMetadata,
    useCommonMediaForAllPlatforms,
    setUseCommonMediaForAllPlatforms,
    handleMediaUpload,
    removeMedia,
    reorderMedia,
    scheduledDate,
    setScheduledDate,
    activeFormTab,
    setActiveFormTab,
    showPlatformSelector,
    setShowPlatformSelector,
    fileInputRef,
    draggedIndex,
    toast,
    setToast,
    platformValidations,
    validationIssues,
    isLaunchDisabled,
    isLaunching,
    xLongFormEnabled,
    setXLongFormEnabled,
    xMaxCharacters,
    handleLaunch,
    handleSaveDraft,
    getMaxImages,
    getMaxVideos,
    handleFileChange,
    handleDragOver,
    handleDrop,
    handleImageDragStart,
    handleImageDragOver,
    handleImageDrop,
    handleImageDragEnd,
    renderTextWithHashtags,
    socialPlatforms,
    viewMode,
    threadEnabled,
    setThreadEnabled,
    threadTweets,
    setThreadTweets,
    isStory,
    setIsStory,
    subreddits,
    setSubreddits,
    searchSubreddit,
  };
}
